/**
 * useRecentlyPurchased — React Query hook for the engineer's recent
 * purchases carousel (DEV-46921).
 *
 * Not deduped in v1 (Noreen 16-07-26 "keep it simple, we will dedupe
 * later"). Capped at 40 items server-side so the same query drives both
 * the Home carousel (first ~10) and the dedicated /recently-purchased
 * page (full list).
 */

import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { api } from '../../services/api'
import { HOME_TAB_ENABLED } from '../../config/features'
import type { RecentlyPurchasedTile } from '../../types'

interface EngineerSession {
  postcode: string
  latitude: number
  longitude: number
}

function readSession(): EngineerSession | null {
  try {
    const raw = sessionStorage.getItem('engineerSession')
    return raw ? (JSON.parse(raw) as EngineerSession) : null
  } catch {
    return null
  }
}

export function useRecentlyPurchased() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>()
  const scope = tenantSlug || 'default'

  // `enabled: HOME_TAB_ENABLED` keeps the hook silent when the build
  // flag is off. Belt-and-braces: today this hook is only called from
  // the route-gated Home page, but gating it here means adding the
  // hook to any other page in future doesn't accidentally start
  // hitting `/api/favourites/recently-purchased/` in prod.
  return useQuery<RecentlyPurchasedTile[]>({
    queryKey: ['recently-purchased', scope],
    queryFn: () => {
      const session = readSession()
      return api.listRecentlyPurchased(
        session?.postcode,
        session?.latitude,
        session?.longitude,
      )
    },
    staleTime: 60 * 1000, // 1 min — tile prices are stored-not-live
    enabled: HOME_TAB_ENABLED,
  })
}
